import {
	classifyLinkedinSenderState,
	LINKEDIN_SENDER_CAN_MESSAGE,
	LinkedinSenderState,
} from "@signalist/shared-types";

import { ConversationMessageType } from "@/enum/conversation.enum";
import { Conversation } from "@/pages/Private/pages/Apollo/schema/get-people";
import { CheckConnectionResult } from "@/pages/Private/pages/Users/schema/get-users";

import { conversationSenderUserId } from "./lastSender.util";

/** The LinkedIn rows that carry a chat id: what we sent into a chat and what arrived from one. */
const LINKEDIN_CHAT_ROW_TYPES = ["message_sent", "message_received"];

/**
 * Whether a conversation row is evidence of a chat its sender can post into — the web twin of the
 * API's `CHAT_EVIDENCE_WHERE` (`linkedin-chat.repository.ts`): a LinkedIn message (never an
 * invitation) that went through a chat. An ACTION row is the invitation or its acceptance, and a
 * `message_scheduled` row has not gone out; counting either made a pending invitation read as
 * "can message", and that message could only fail (PREC-4216).
 */
export const isLinkedinChatEvidence = (conv: Conversation): boolean =>
	conv.messageType === ConversationMessageType.MESSAGE &&
	LINKEDIN_CHAT_ROW_TYPES.includes(conv.type) &&
	!!conv.threadId &&
	!!conv.senderLinkedinUrl;

/**
 * The users who hold a LinkedIn chat with this lead (a chat the lead opened counts, PREC-4221),
 * by the conversations the page already loaded. Only chat evidence counts
 * ({@link isLinkedinChatEvidence}): an invitation-only conversation is not a chat (PREC-4216).
 */
export const getUserIdsWithLinkedinConversation = (
	conversations: Conversation[],
	listPersonId: number
): Set<number> => {
	const userIds = new Set<number>();

	for (const conv of conversations) {
		const userId = conversationSenderUserId(conv);

		if (userId && conv.listPersonId === listPersonId && isLinkedinChatEvidence(conv)) {
			userIds.add(userId);
		}
	}

	return userIds;
};

/** What the composer knows about each LinkedIn sender it asked about, for one lead (PREC-4216). */
export interface LinkedinSenderStates {
	/** An ordinary message is delivered now: connected, or a chat exists (CONNECTED / CHAT). */
	connectedUserIds: Set<number>;
	/** This sender's own invitation is open and there is no chat, with the invitation's deadline. */
	pendingInvitations: Map<number, string | null>;
	/** The check failed: nothing is known, so the sender can neither message nor invite. */
	unknownUserIds: Set<number>;
}

export const NO_SENDER_STATES: LinkedinSenderStates = {
	connectedUserIds: new Set(),
	pendingInvitations: new Map(),
	unknownUserIds: new Set(),
};

/** One sender's state: the API's answer, with a chat the page shows as chat evidence. */
const resolveSenderState = (result: CheckConnectionResult, hasLocalChat: boolean) =>
	classifyLinkedinSenderState({
		connected: result.state ? result.state === LinkedinSenderState.CONNECTED : result.connected,
		hasChat: result.state === LinkedinSenderState.CHAT || hasLocalChat,
		invitationPending: result.state === LinkedinSenderState.INVITATION_PENDING,
	});

/**
 * The composer's per-sender view of a successful `users/check-connection`: the API's state per
 * sender, decided by the shared `classifyLinkedinSenderState` (a chat the loaded thread shows
 * counts as a chat too), then split into who can message, whose invitation is pending.
 */
export const senderStatesFromCheck = (
	results: CheckConnectionResult[],
	usersWithChat: Set<number>
): LinkedinSenderStates => {
	const states: LinkedinSenderStates = {
		connectedUserIds: new Set(),
		pendingInvitations: new Map(),
		unknownUserIds: new Set(),
	};

	for (const result of results) {
		const state = resolveSenderState(result, usersWithChat.has(result.userId));

		if (LINKEDIN_SENDER_CAN_MESSAGE[state]) {
			states.connectedUserIds.add(result.userId);
		} else if (state === LinkedinSenderState.INVITATION_PENDING) {
			states.pendingInvitations.set(result.userId, result.invitationDeadline ?? null);
		}
	}

	return states;
};

/**
 * A failed check answers nothing, and that is not "connected" nor "no invitation": every asked
 * sender is unknown (fail closed, PREC-4216). Counting the loaded conversations here, as before,
 * made a sender whose invitation was pending read as connected.
 */
export const senderStatesForFailedCheck = (userIds: number[]): LinkedinSenderStates => ({
	connectedUserIds: new Set(),
	pendingInvitations: new Map(),
	unknownUserIds: new Set(userIds),
});
